import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, FileEdit, Save, X, Undo, Redo, Search, WrapText, ZoomIn, ZoomOut } from 'lucide-react';
import { loader } from '@monaco-editor/react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { t } from '../../i18n';

// Import Monaco setup (must be before Editor)
import { monaco } from '../../../../monacoSetup';

// Configure loader to use local monaco
loader.config({ monaco });

// Lazy import Editor to ensure monaco is configured first
const Editor = React.lazy(() => import('@monaco-editor/react').then(mod => ({ default: mod.Editor })));

// Supported encodings for file reading
const SUPPORTED_ENCODINGS = ['UTF-8', 'GBK', 'GB18030', 'Big5', 'Shift_JIS', 'Windows-1252'];

// Binary file extensions that cannot be edited
const BINARY_EXTENSIONS = [
  'exe', 'dll', 'so', 'bin', 'iso', 'img', 'zip', 'rar', '7z', 'tar', 'gz',
  'mp3', 'wav', 'flac', 'mp4', 'mkv', 'avi', 'mov', 'webm',
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'ico', 'pdf'
];

// Map file extensions to Monaco language IDs
const EXTENSION_TO_LANGUAGE: Record<string, string> = {
  'js': 'javascript', 'jsx': 'javascript', 'mjs': 'javascript',
  'ts': 'typescript', 'tsx': 'typescript',
  'json': 'json', 'jsonc': 'json',
  'html': 'html', 'htm': 'html',
  'css': 'css', 'scss': 'scss', 'less': 'less',
  'xml': 'xml', 'svg': 'xml',
  'md': 'markdown', 'markdown': 'markdown',
  'yaml': 'yaml', 'yml': 'yaml',
  'py': 'python',
  'go': 'go',
  'java': 'java',
  'c': 'c', 'h': 'c',
  'cpp': 'cpp', 'hpp': 'cpp', 'cc': 'cpp',
  'cs': 'csharp',
  'php': 'php',
  'rb': 'ruby',
  'rs': 'rust',
  'sql': 'sql',
  'sh': 'shell', 'bash': 'shell',
  'bat': 'bat', 'cmd': 'bat',
  'ps1': 'powershell',
  'ini': 'ini', 'conf': 'ini',
  'dockerfile': 'dockerfile',
};

// Get Monaco language from filename
const getLanguage = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return EXTENSION_TO_LANGUAGE[ext] || 'plaintext';
};

// Base64 encode helper
const encodeBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// Editor reference type
type IStandaloneCodeEditor = Parameters<import('@monaco-editor/react').OnMount>[0];

const EditModal: React.FC = () => {
  const { editorFile, closeEditor, fs, refresh } = useFileManagerStore();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [encoding, setEncoding] = useState('UTF-8');
  const [dirty, setDirty] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  const [wordWrap, setWordWrap] = useState<'on' | 'off'>('on');

  const editorRef = useRef<IStandaloneCodeEditor | null>(null);

  // Load file content
  useEffect(() => {
    if (!editorFile) return;

    const ext = editorFile.name.split('.').pop()?.toLowerCase() || '';
    if (BINARY_EXTENSIONS.includes(ext)) {
      setError(t('editBinaryNotSupported'));
      return;
    }

    const loadFile = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fs.readFile(editorFile.path);
        const binaryStr = atob(result.data);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }

        let decoder: TextDecoder;
        try {
          decoder = new TextDecoder(encoding);
        } catch {
          decoder = new TextDecoder('UTF-8');
        }

        setContent(decoder.decode(bytes));
        setDirty(false);
      } catch (err) {
        setError(t('editLoadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadFile();
  }, [editorFile, encoding, fs]);

  if (!editorFile) return null;

  // Save file
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const encoder = new TextEncoder();
      const bytes = encoder.encode(content);
      await fs.writeFile(editorFile.path, encodeBase64(bytes), 0, true, true);
      await refresh();
      setDirty(false);
    } catch (err) {
      setError(t('editSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Editor mount handler
  const handleEditorMount = (editor: IStandaloneCodeEditor) => {
    editorRef.current = editor;
    editor.focus();

    // Add Ctrl+S shortcut
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      handleSave();
    });
  };

  // Toolbar actions
  const handleUndo = () => {
    const editor = editorRef.current;
    if (editor) {
      editor.focus();
      editor.trigger('source', 'undo', null);
    }
  };

  const handleRedo = () => {
    const editor = editorRef.current;
    if (editor) {
      editor.focus();
      editor.trigger('source', 'redo', null);
    }
  };

  const handleFind = () => {
    const editor = editorRef.current;
    if (editor) {
      editor.focus();
      editor.trigger('source', 'actions.find', null);
    }
  };

  const toggleWrap = () => setWordWrap(prev => prev === 'on' ? 'off' : 'on');
  const zoomIn = () => setFontSize(prev => Math.min(32, prev + 2));
  const zoomOut = () => setFontSize(prev => Math.max(8, prev - 2));

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
      <div className="bg-[#1e1e1e] w-[90vw] h-[90vh] rounded-lg shadow-2xl border border-[#3e3e42] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#252526] border-b border-[#3e3e42]">
          <div className="flex items-center gap-3">
            <FileEdit size={18} className="text-[#007acc]" />
            <span className="text-[#ccc] font-medium truncate max-w-[300px]">{editorFile.name}</span>
            {dirty && <span className="text-[#007acc]">*</span>}
          </div>
          <div className="flex items-center gap-2">
            {!error && (
              <select
                value={encoding}
                onChange={(e) => setEncoding(e.target.value)}
                className="bg-[#3c3c3c] border border-[#3e3e42] text-xs text-[#ccc] rounded px-2 py-1"
              >
                {SUPPORTED_ENCODINGS.map(enc => <option key={enc} value={enc}>{enc}</option>)}
              </select>
            )}
            <button
              onClick={handleSave}
              disabled={saving || loading || !!error || !dirty}
              className="flex items-center px-3 py-1.5 rounded bg-[#0e639c] text-white text-xs font-medium hover:bg-[#1177bb] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={14} className="mr-1.5" />
              {saving ? t('editSaving') : t('editSave')}
            </button>
            <button onClick={closeEditor} className="p-1 hover:bg-[#3c3c3c] rounded">
              <X size={20} className="text-[#ccc]" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        {!error && (
          <div className="flex items-center gap-1 px-2 py-1 bg-[#333] border-b border-[#3e3e42]">
            <button
              type="button"
              title={t('editUndo')}
              onClick={handleUndo}
              className="p-1.5 rounded transition-colors hover:bg-[#3c3c3c] text-[#ccc]"
            >
              <Undo size={16} />
            </button>
            <button
              type="button"
              title={t('editRedo')}
              onClick={handleRedo}
              className="p-1.5 rounded transition-colors hover:bg-[#3c3c3c] text-[#ccc]"
            >
              <Redo size={16} />
            </button>
            <div className="w-px h-4 bg-[#555] mx-1" />
            <button
              type="button"
              title={t('editFind')}
              onClick={handleFind}
              className="p-1.5 rounded transition-colors hover:bg-[#3c3c3c] text-[#ccc]"
            >
              <Search size={16} />
            </button>
            <div className="w-px h-4 bg-[#555] mx-1" />
            <button
              type="button"
              title={t('editToggleWrap')}
              onClick={toggleWrap}
              className={`p-1.5 rounded transition-colors ${wordWrap === 'on' ? 'bg-[#0e639c] text-white' : 'hover:bg-[#3c3c3c] text-[#ccc]'}`}
            >
              <WrapText size={16} />
            </button>
            <div className="w-px h-4 bg-[#555] mx-1" />
            <button
              type="button"
              title={t('editZoomOut')}
              onClick={zoomOut}
              className="p-1.5 rounded transition-colors hover:bg-[#3c3c3c] text-[#ccc]"
            >
              <ZoomOut size={16} />
            </button>
            <span className="text-xs text-[#888] min-w-[24px] text-center">{fontSize}</span>
            <button
              type="button"
              title={t('editZoomIn')}
              onClick={zoomIn}
              className="p-1.5 rounded transition-colors hover:bg-[#3c3c3c] text-[#ccc]"
            >
              <ZoomIn size={16} />
            </button>
          </div>
        )}

        {/* Editor Area */}
        <div className="flex-1 overflow-hidden relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1e1e1e]/80 z-10">
              <div className="w-8 h-8 border-2 border-[#007acc] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {error ? (
            <div className="flex flex-col items-center justify-center h-full text-[#ccc]">
              <AlertCircle size={48} className="mb-4 opacity-50" />
              <p>{error}</p>
            </div>
          ) : (
            <React.Suspense fallback={
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-2 border-[#007acc] border-t-transparent rounded-full animate-spin" />
              </div>
            }>
              <Editor
                height="100%"
                language={getLanguage(editorFile.name)}
                theme="vs-dark"
                value={content}
                onChange={(value) => {
                  if (value !== undefined) {
                    setContent(value);
                    setDirty(true);
                  }
                }}
                onMount={handleEditorMount}
                options={{
                  fontSize,
                  wordWrap,
                  minimap: { enabled: false },
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  lineNumbers: 'on',
                  renderLineHighlight: 'line',
                  tabSize: 2,
                  insertSpaces: true,
                }}
              />
            </React.Suspense>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditModal;
